import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Play, Mic2, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { personalInfo, images, awards, articles, videos, stats } from '../data/mockData';

const HomePage = () => {
  const featuredAwards = awards.filter(award => award.highlight).slice(0, 2);
  const featuredArticles = articles.filter(article => article.featured).slice(0, 2);

  return (
    <div className="min-h-screen bg-[#0f1419]">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f1419] via-[#1a1f2e] to-[#0f1419]" />
        
        {/* Decorative Elements */}
        <div className="absolute top-20 right-0 w-96 h-96 bg-[#d4af37]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-0 w-64 h-64 bg-[#d4af37]/5 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="space-y-8">
              <div className="space-y-4">
                <p className="text-[#d4af37] font-medium tracking-widest uppercase text-sm">
                  Award-Winning Journalist
                </p>
                <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-[#f5f5f0] leading-tight">
                  Jerry<br />
                  <span className="text-[#d4af37]">Fisayo-Bambi</span>
                </h1>
                <p className="text-xl text-[#a0a0a0] max-w-lg leading-relaxed">
                  {personalInfo.shortBio}
                </p>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/work">
                  <Button className="bg-[#d4af37] hover:bg-[#b8962d] text-[#0f1419] font-semibold px-8 py-6 rounded-full text-base transition-transform hover:scale-105">
                    View My Work
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button variant="outline" className="border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37]/10 px-8 py-6 rounded-full text-base">
                    About Me
                  </Button>
                </Link>
              </div>

              {/* Quick Stats */}
              <div className="flex flex-wrap gap-8 pt-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#d4af37]">{stats.awards}</p>
                  <p className="text-[#a0a0a0] text-sm uppercase tracking-wide">Awards</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#d4af37]">{stats.years}</p>
                  <p className="text-[#a0a0a0] text-sm uppercase tracking-wide">Years</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#d4af37]">{stats.stories}</p>
                  <p className="text-[#a0a0a0] text-sm uppercase tracking-wide">Bylines</p>
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-tr from-[#d4af37]/20 to-transparent rounded-2xl blur-2xl" />
              <div className="relative rounded-2xl overflow-hidden border border-[#d4af37]/20">
                <img
                  src={images.hero}
                  alt="Jerry Fisayo-Bambi"
                  className="w-full h-[600px] object-cover object-top"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0f1419] to-transparent h-32" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Awards Section */}
      <section className="py-24 bg-[#0a0d10]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between mb-12">
            <div>
              <p className="text-[#d4af37] font-medium tracking-widest uppercase text-sm mb-2">
                Recognition
              </p>
              <h2 className="font-serif text-4xl font-bold text-[#f5f5f0]">
                Awards & Honours
              </h2>
            </div>
            <Link to="/about" className="hidden md:flex items-center text-[#d4af37] hover:text-[#b8962d] transition-colors">
              View All <ArrowRight className="ml-2" size={18} />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {featuredAwards.map((award) => (
              <div
                key={award.id}
                className="group relative p-8 bg-[#1a1f2e] rounded-xl border border-[#2a3441] hover:border-[#d4af37]/50 transition-all duration-300"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-[#d4af37]/10 flex items-center justify-center flex-shrink-0">
                    <Award className="text-[#d4af37]" size={24} />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-3">
                      <h3 className="font-serif text-xl font-semibold text-[#f5f5f0]">
                        {award.title}
                      </h3>
                      <span className="px-3 py-1 bg-[#d4af37]/10 text-[#d4af37] text-xs font-medium rounded-full">
                        {award.year}
                      </span>
                    </div>
                    <p className="text-[#a0a0a0] leading-relaxed">
                      {award.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What I Do Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-[#d4af37] font-medium tracking-widest uppercase text-sm mb-2">
              Services
            </p>
            <h2 className="font-serif text-4xl font-bold text-[#f5f5f0]">
              What I Do
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <FileText size={32} />,
                title: 'Journalism',
                description: 'In-depth reporting and storytelling that brings African narratives to global audiences through Euronews and Inspire Africa.'
              },
              {
                icon: <Mic2 size={32} />,
                title: 'Event Moderation',
                description: 'Professional moderation for high-profile conferences, panels, and corporate events worldwide.'
              },
              {
                icon: <Play size={32} />,
                title: 'Media Strategy',
                description: 'Strategic communication consulting and media campaign development for organizations and brands.'
              }
            ].map((service, index) => (
              <div
                key={index}
                className="group p-8 bg-[#1a1f2e] rounded-xl border border-[#2a3441] hover:border-[#d4af37]/50 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-16 h-16 rounded-xl bg-[#d4af37]/10 flex items-center justify-center text-[#d4af37] mb-6 group-hover:bg-[#d4af37] group-hover:text-[#0f1419] transition-all duration-300">
                  {service.icon}
                </div>
                <h3 className="font-serif text-xl font-semibold text-[#f5f5f0] mb-3">
                  {service.title}
                </h3>
                <p className="text-[#a0a0a0] leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Work Preview */}
      <section className="py-24 bg-[#0a0d10]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between mb-12">
            <div>
              <p className="text-[#d4af37] font-medium tracking-widest uppercase text-sm mb-2">
                Recent Work
              </p>
              <h2 className="font-serif text-4xl font-bold text-[#f5f5f0]">
                Featured Articles
              </h2>
            </div>
            <Link to="/work" className="hidden md:flex items-center text-[#d4af37] hover:text-[#b8962d] transition-colors">
              View All <ArrowRight className="ml-2" size={18} />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {featuredArticles.map((article) => (
              <a
                key={article.id}
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block"
              >
                <div className="relative rounded-xl overflow-hidden mb-4">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-[#d4af37] text-[#0f1419] text-xs font-semibold rounded-full">
                      {article.category}
                    </span>
                  </div>
                </div>
                <h3 className="font-serif text-xl font-semibold text-[#f5f5f0] group-hover:text-[#d4af37] transition-colors mb-2">
                  {article.title}
                </h3>
                <p className="text-[#a0a0a0] line-clamp-2">
                  {article.excerpt}
                </p>
              </a>
            ))}
          </div>

          <div className="mt-12 text-center md:hidden">
            <Link to="/work">
              <Button className="bg-[#d4af37] hover:bg-[#b8962d] text-[#0f1419] font-semibold px-8 py-6 rounded-full">
                View All Work
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#f5f5f0] mb-6">
            Let's Work Together
          </h2>
          <p className="text-xl text-[#a0a0a0] mb-10 max-w-2xl mx-auto">
            Looking for a moderator, media strategist, or compelling storyteller? 
            Let's create something impactful together.
          </p>
          <Link to="/work">
            <Button className="bg-[#d4af37] hover:bg-[#b8962d] text-[#0f1419] font-semibold px-10 py-6 rounded-full text-lg transition-transform hover:scale-105">
              Get in Touch
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;